/* Borderline winter 26/27 · "Buy the set" on the product page (P6, blocks/bl-pdp-set.liquid, owner 10/20).
   A jacket + pants product sells both pieces in one click without a set variant: two size rows that follow the size
   chosen above until changed here, the two prices added, one button that posts both variants to /cart/add.js and
   fires the theme's CartLinesUpdateEvent (the cart drawer opens) — the add path of assets/bl-lookbook.js. Each piece
   keeps its own stock. Pre-order keeps every size open; otherwise a size with no stock is greyed. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

class BlPdpSet extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {any} */
  #data = { v: [], words: {} };
  /** @type {Record<string, string>} */
  #pick = { jacket: '', pants: '' };
  /** the shopper chose a size here: the rows stop following the size above */
  #touched = false;
  #busy = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    try {
      this.#data = JSON.parse((this.querySelector('script[data-pset]') || {}).textContent || '{}');
    } catch (error) {
      this.#data = { v: [], words: {} };
    }
    if (this.#data.current) this.#follow(this.#data.current);
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const cell = target ? target.closest('[data-size]') : null;
      const row = cell ? cell.closest('[data-piece]') : null;
      if (cell instanceof HTMLElement && row instanceof HTMLElement && !cell.hasAttribute('disabled')) {
        this.#pick[row.dataset.piece || ''] = cell.dataset.size || '';
        this.#touched = true;
        this.#paint();
        return;
      }
      if (target && target.closest('[data-pset-add]')) this.#add();
    }, { signal });
    // the size chosen above: at once from the option boxes, and from the variant Horizon fetched (finder "Select",
    // the specs table heads) as assets/bl-pdp.js re-sends it (bl:pdp:variant)
    document.addEventListener('click', (event) => {
      if (this.#touched) return;
      const target = event.target instanceof Element ? event.target : null;
      const pos = Number(this.#data.sizeIx) + 1;
      const box = target ? target.closest(`.bl-opt[data-pos="${pos}"] .bl-opt__vals .opt`) : null;
      if (box instanceof HTMLElement && !box.classList.contains('is-out') && box.dataset.value) this.#follow(box.dataset.value);
    }, { signal });
    document.addEventListener('bl:pdp:variant', (event) => {
      if (this.#touched) return;
      const variant = /** @type {any} */ (event).detail && /** @type {any} */ (event).detail.variant;
      const ix = Number(this.#data.sizeIx);
      const size = variant ? (Array.isArray(variant.options) ? variant.options[ix] : variant[`option${ix + 1}`]) : '';
      if (size) this.#follow(size);
    }, { signal });
    this.#paint();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** Both rows to this size. @param {string} size */
  #follow(size) {
    this.#pick.jacket = size;
    this.#pick.pants = size;
    this.#paint();
  }

  /** @param {string} piece @param {string} size */
  #variant(piece, size) {
    return (this.#data.v || []).find((/** @type {any} */ v) => v.style === piece && v.size === size) || null;
  }

  /** Can this piece be bought in this size? @param {string} piece @param {string} size */
  #state(piece, size) {
    const v = this.#variant(piece, size);
    if (!v) return { v: null, ok: false, why: 'notMade' };
    if (!this.#data.preorder && (!v.available || (v.q !== null && v.q <= 0))) return { v, ok: false, why: 'soldOut' };
    return { v, ok: true, why: '' };
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.#data.moneyFormat || '${{amount}}', this.#data.currency || 'CAD');
  }

  #paint() {
    const w = this.#data.words || {};
    for (const row of this.querySelectorAll('[data-piece]')) {
      const piece = /** @type {HTMLElement} */ (row).dataset.piece || '';
      for (const cell of row.querySelectorAll('[data-size]')) {
        const size = /** @type {HTMLElement} */ (cell).dataset.size || '';
        const st = this.#state(piece, size);
        cell.toggleAttribute('disabled', !st.ok);
        cell.setAttribute('aria-pressed', String(this.#pick[piece] === size));
      }
      const chosen = row.querySelector('[data-pset-chosen]');
      if (chosen) chosen.textContent = this.#pick[piece] || '';
    }
    const button = this.querySelector('[data-pset-add]');
    const msg = this.querySelector('[data-pset-msg]');
    const j = this.#pick.jacket ? this.#state('jacket', this.#pick.jacket) : null;
    const p = this.#pick.pants ? this.#state('pants', this.#pick.pants) : null;
    let note = '';
    if (!j || !p) note = w.choose || '';
    else if (!j.ok) note = String((j.why === 'notMade' ? w.notMade : w.soldOut) || '').replace('[piece]', w.jacket || 'Jacket').replace('[size]', this.#pick.jacket);
    else if (!p.ok) note = String((p.why === 'notMade' ? w.notMade : w.soldOut) || '').replace('[piece]', w.pants || 'Pants').replace('[size]', this.#pick.pants);
    const ready = !note && j && p && j.v && p.v;
    if (button instanceof HTMLButtonElement) {
      button.disabled = !ready || this.#busy;
      button.textContent = ready ? `${w.button || 'Add the set'} · ${this.#money(j.v.price + p.v.price)}` : w.button || 'Add the set';
    }
    if (msg instanceof HTMLElement && !this.#busy) {
      msg.textContent = note;
      msg.classList.remove('is-ok', 'is-error');
    }
  }

  async #add() {
    if (this.#busy) return;
    const j = this.#state('jacket', this.#pick.jacket);
    const p = this.#state('pants', this.#pick.pants);
    if (!j.ok || !p.ok || !j.v || !p.v) return;
    const ids = [j.v.id, p.v.id];
    const w = this.#data.words || {};
    const button = this.querySelector('[data-pset-add]');
    const msg = this.querySelector('[data-pset-msg]');
    this.#busy = true;
    if (button instanceof HTMLButtonElement) button.disabled = true;
    this.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: ids.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    let ok = false;
    let message = '';
    // Horizon's `Theme` is a global const of snippets/scripts.liquid (not a window property)
    // eslint-disable-next-line no-undef
    const routes = (typeof Theme !== 'undefined' && /** @type {any} */ (Theme).routes) || { cart_add_url: '/cart/add.js', cart_url: '/cart' };
    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })), sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
      const detail = { items: cart.items, source: 'bl-pdp-set', sourceId: this.id, itemCount: ids.length };
      if (result.status) {
        message = result.description || result.message || w.error || '';
        this.dispatchEvent(new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID', detail: { description: result.description, errors: result.errors } }));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, didError: true } });
      } else {
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, sections: result.sections, didError: false } });
        ok = true;
      }
    } catch (error) {
      deferred.reject(error);
      message = w.error || '';
      this.dispatchEvent(new CartErrorEvent({ error: error instanceof Error ? error.message : 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
      this.#paint();
      if (msg instanceof HTMLElement) {
        msg.textContent = ok ? w.added || '' : message;
        msg.classList.toggle('is-ok', ok);
        msg.classList.toggle('is-error', !ok);
      }
    }
  }
}

if (!customElements.get('bl-pdp-set')) customElements.define('bl-pdp-set', BlPdpSet);
